/**
 * TrademarkManagement Component
 * 
 * Admin interface for managing trademark applications and analysis
 */
import React, { useState, useEffect } from 'react';
import { 
  FileText,
  Search,
  Filter,
  Eye,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Calendar
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import { applicationService } from '../../../services/api/applicationService';
import { trademarkService } from '../../../services/api/trademarkService';

const TrademarkManagement = () => {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch all applications from backend
      const response = await applicationService.getAllApplications();
      // API returns array directly, not wrapped in applications property
      setApplications(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load trademark applications');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'registered':
      case 'filed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
      case 'quote_requested':
      case 'quote_sent':
      case 'payment_pending':
      case 'pending_attorney_filing':
      case 'filing_in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
      case 'rejected':
      case 'cancelled':
      case 'refunded':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'registered':
      case 'filed':
        return 'bg-green-100 text-green-800';
      case 'quote_requested':
        return 'bg-blue-100 text-blue-800';
      case 'quote_sent':
        return 'bg-amber-100 text-amber-800';
      case 'pending':
      case 'payment_pending':
      case 'pending_attorney_filing':
      case 'filing_in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'rejected':
      case 'cancelled':
      case 'refunded':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const STATUS_LABELS = {
    active: 'Active',
    draft: 'Draft',
    quote_requested: 'Quote Requested',
    quote_sent: 'Quote Sent',
    payment_pending: 'Payment Pending',
    payment_completed: 'Payment Completed',
    pending_attorney_filing: 'Pending Filing',
    filing_in_progress: 'Filing in Progress',
    filed: 'Filed',
    registered: 'Registered',
    completed: 'Completed',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
  };

  const getStatusLabel = (status) => STATUS_LABELS[status] || status || 'pending';

  const filteredApplications = applications.filter(app => {
    const matchesSearch = !searchTerm || 
      app.trademarkName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <H3 className="mb-2 text-red-900">Error Loading Applications</H3>
          <Text className="text-gray-600 mb-4">{error}</Text>
          <Button onClick={fetchApplications} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <H3 className="mb-2">Trademark Applications</H3>
          <Text className="text-gray-600">
            Manage and monitor all trademark applications
          </Text>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchApplications}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by trademark name, applicant, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="quote_requested">Quote Requested</option>
                <option value="quote_sent">Quote Sent</option>
                <option value="pending_attorney_filing">Pending Filing</option>
                <option value="filing_in_progress">Filing in Progress</option>
                <option value="filed">Filed</option>
                <option value="registered">Registered</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card>
        <CardContent className="p-0">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <Text className="text-lg font-medium text-gray-900 mb-2">No applications found</Text>
              <Text className="text-gray-500">
                No trademark applications match your current search criteria.
              </Text>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applicant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <FileText className="h-8 w-8 text-gray-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {application.trademarkName || 'Unnamed Trademark'}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {application.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {application.applicantName || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {application.applicantEmail || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(application.status)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(application.status)}`}>
                            {getStatusLabel(application.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {formatDate(application.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination placeholder */}
      {filteredApplications.length > 0 && (
        <div className="flex items-center justify-between">
          <Text className="text-sm text-gray-700">
            Showing {filteredApplications.length} applications
          </Text>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrademarkManagement;
